import { Alert, Box, Icon, IconButton, List, ListItem, ListItemText, Tooltip, Typography } from "@mui/material";
import { MessageNotification } from "../../../_components/box/message-notification";
import { StandardButton } from "../../../_components/button/primary.button";
import { StandardPanel } from "../../../_components/panel/standard.panel";

type TurnActionType = "accept" | "skip";

interface SelectedTurnItem {
    id: number;
    lotNo: number;
    forestCode: string;
    product: string;
    volume: number;
}

interface AuctionAnnualCustomerTurnOrderProps {
    loading: boolean;
    isRegistered: boolean;
    roundNumber: number;
    canClientAct: boolean;
    positionsUntilTurn?: number | null;
    hasPendingSelection: boolean;
    turnRequestLoading: boolean;
    allowance: number;
    selectedVolume: number;
    remainingVolume: number;
    bauLeftEntries: { code: string; left: number; isLow: boolean }[];
    pendingItemIds: number[];
    selectedItemsForRound: SelectedTurnItem[];
    onRequestTurnAction: (action: TurnActionType) => void;
    onRemovePendingItem: (itemId: number) => void;
}

export function AuctionAnnualCustomerTurnOrder({
    loading,
    isRegistered,
    roundNumber,
    canClientAct,
    positionsUntilTurn,
    hasPendingSelection,
    turnRequestLoading,
    allowance,
    selectedVolume,
    remainingVolume,
    bauLeftEntries,
    pendingItemIds,
    selectedItemsForRound,
    onRequestTurnAction,
    onRemovePendingItem,
}: AuctionAnnualCustomerTurnOrderProps) {
    return (
        <StandardPanel title="Turn Order" loading={loading}>
            {!isRegistered ? (
                <MessageNotification message="You are not registered on the active annual catalogue." />
            ) : (
                <Box sx={{ display: "grid", gap: 1 }}>
                    {(() => {
                        const positions = typeof positionsUntilTurn === "number" ? positionsUntilTurn : null;
                        const isMyTurn = canClientAct || positions === 0;
                        if (isMyTurn) {
                            return (
                                <Alert severity="success" icon={false} sx={{ py: 0.75, fontSize: "1.05rem", fontWeight: 700 }}>
                                    It is your turn{canClientAct ? " — make your selection below." : "."}
                                </Alert>
                            );
                        }
                        if (positions === 1) {
                            return (
                                <Alert severity="warning" icon={false} sx={{ py: 0.75, fontSize: "1.05rem", fontWeight: 700 }}>
                                    You are next — get ready!
                                </Alert>
                            );
                        }
                        if (positions != null && positions > 1) {
                            return (
                                <Alert severity="info" icon={false} sx={{ py: 0.75, fontSize: "1.05rem" }}>
                                    Waiting for your turn — <strong>{positions} customers</strong> ahead of you.
                                </Alert>
                            );
                        }
                        return (
                            <Alert severity="info" icon={false} sx={{ py: 0.75, fontSize: "1.05rem" }}>
                                Waiting for your turn.
                            </Alert>
                        );
                    })()}
                    <Typography variant="subtitle1" color="text.secondary">
                        Round {roundNumber}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Allowance: {allowance} • Selected: {selectedVolume} • Left: {remainingVolume}
                    </Typography>
                    {bauLeftEntries.length > 0 ? (
                        <Typography variant="body1" color="text.secondary" display="block">
                            BAU left:{" "}
                            {bauLeftEntries.map((entry, index) => (
                                <Box
                                    key={`bau-left-${entry.code}`}
                                    component="span"
                                    sx={{ color: entry.isLow ? "error.main" : "inherit", fontWeight: entry.isLow ? 700 : 400 }}
                                >
                                    {index > 0 ? ", " : ""}
                                    {entry.code}: {entry.left}
                                </Box>
                            ))}
                        </Typography>
                    ) : null}
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Selected in this round
                        </Typography>
                        {selectedItemsForRound.length === 0 ? (
                            <Typography variant="body1" color="text.secondary">
                                No items selected.
                            </Typography>
                        ) : (
                            <List disablePadding>
                                {selectedItemsForRound.map((item) => (
                                    <ListItem key={item.id} disablePadding sx={{ py: 0.25 }}>
                                        <Tooltip title={pendingItemIds.includes(item.id) && canClientAct ? "Remove from current selection" : "Locked"}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={!pendingItemIds.includes(item.id) || !canClientAct}
                                                    onClick={() => onRemovePendingItem(item.id)}
                                                >
                                                    <Icon fontSize="small">delete</Icon>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <ListItemText
                                            primaryTypographyProps={{ variant: "body1" }}
                                            primary={`Lot ${item.lotNo} • BAU ${item.forestCode} • ${item.product} • Vol ${item.volume}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                    <StandardButton
                        icon={hasPendingSelection ? "check" : "restart_alt"}
                        variant={hasPendingSelection ? "success" : "info"}
                        disabled={!canClientAct || turnRequestLoading}
                        onClick={() => onRequestTurnAction(hasPendingSelection ? "accept" : "skip")}
                    >
                        {hasPendingSelection ? "Accept Selection" : "Skip Turn (Carry Over)"}
                    </StandardButton>
                </Box>
            )}
        </StandardPanel>
    );
}
